import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { formatDate, formatTime } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Dialog, Eyebrow, Pill, Skeleton, useToast } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import { dayTime, formatCountdown, relativeTime } from '../calendar/dates';
import { SettingsPageHeader } from './SettingsFrame';
import {
  formatPhone,
  useEndWhatsapp,
  useRefreshWhatsapp,
  useStartWhatsapp,
  useWhatsappStatus,
  type WhatsappState,
  type WhatsappStatus,
} from './connections';
import styles from './WhatsAppSettingsPage.module.css';

/** D-04: the pairing runs through WhatsApp's own "Verknüpfte Geräte", there is no BRIMO phone app. */
const STEPS = [
  <>WhatsApp auf dem Diensthandy öffnen.</>,
  <>
    Unter <span className={styles.stepStrong}>Einstellungen · Verknüpfte Geräte</span> auf „Gerät hinzufügen“
    tippen.
  </>,
  <>Kamera auf diesen Code halten. Die Session baut sich automatisch auf.</>,
];
const STEPS_SHORT = ['WhatsApp öffnen.', 'Einstellungen · Verknüpfte Geräte · Gerät hinzufügen.'];

/** „3 Stunden 12 Minuten" — the time since the last heartbeat (E12). */
export function durationSince(iso: string, now: Date = new Date()): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'wenigen Sekunden';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`);
  if (rest > 0 || hours === 0) parts.push(`${rest} ${rest === 1 ? 'Minute' : 'Minuten'}`);
  return parts.join(' ');
}

/** The remaining validity of the code (ms), re-evaluated every second. */
export function useCountdown(expiresAt: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);
  if (!expiresAt) return null;
  return new Date(expiresAt).getTime() - now;
}

/** The code area of E08/E09: no session yet, the bridge boots the browser (D-55), the code, or the expired code. */
export type QrMode = 'none' | 'starting' | 'qr' | 'expired';

export function qrModeOf(state: WhatsappState): QrMode {
  return state === 'qr' || state === 'expired' || state === 'starting' ? state : 'none';
}

export interface QrBoxProps {
  mode: QrMode;
  qrDataUrl: string | null;
  progressText: string | null;
  size: number;
  busy: boolean;
  onStart: () => void;
  onRefresh: () => void;
}

/**
 * The QR frame of E08/E09 (and EM4): the code (the latest one of every poll — the bridge rotates it), dimmed
 * with an overlay when there is none or it expired, or the spinner block while the bridge starts the session.
 */
export function QrBox({ mode, qrDataUrl, progressText, size, busy, onStart, onRefresh }: QrBoxProps) {
  if (mode === 'starting') {
    return (
      <div className={styles.qrBox} style={{ width: size, height: size }}>
        <div className={styles.qrStarting} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          <span className={styles.qrStartingText}>{progressText ?? 'Session wird gestartet'}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.qrBox} style={{ width: size, height: size }}>
      {qrDataUrl ? (
        <img
          src={qrDataUrl}
          alt="QR-Code zum Verbinden der WhatsApp-Session"
          className={clsx(styles.qrImage, mode === 'expired' && styles.qrImageExpired)}
        />
      ) : (
        <div className={clsx(styles.qrPlaceholder, styles.qrImageExpired)} aria-hidden="true" />
      )}
      {mode !== 'qr' ? (
        <div className={styles.qrOverlay}>
          <span className={styles.qrOverlayTitle}>
            {mode === 'expired' ? 'Code abgelaufen' : 'Keine Session offen'}
          </span>
          <Button loading={busy} onClick={mode === 'expired' ? onRefresh : onStart}>
            {mode === 'expired' ? 'Neuen Code laden' : 'QR-Code anzeigen'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function StepList({ dimmed }: { dimmed: boolean }) {
  const items = dimmed ? STEPS_SHORT : STEPS;
  return (
    <ol className={clsx(styles.steps, dimmed && styles.stepsDimmed)}>
      {items.map((step, index) => (
        <li key={index} className={styles.step}>
          <span className={styles.stepNumber} aria-hidden="true">
            {index + 1}
          </span>
          <span className={styles.stepText}>{step}</span>
        </li>
      ))}
    </ol>
  );
}

interface ViewProps {
  status: WhatsappStatus;
  state: WhatsappState;
  msLeft: number | null;
  busy: boolean;
  error: string | null;
  onStart: () => void;
  onRefresh: () => void;
  onCancelPairing: () => void;
  onEnd: () => void;
  onCheck: () => void;
  checking: boolean;
}

/** E08 / E09 (with the "none" state and the bridge's "starting" state as further faces of the same layout). */
function QrView({ status, state, msLeft, busy, error, onStart, onRefresh }: ViewProps) {
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const mode = qrModeOf(state);
  return (
    <div className={styles.qrCard}>
      <div className={styles.qrColumn}>
        <QrBox
          mode={mode}
          qrDataUrl={status.qrDataUrl}
          progressText={status.progressText}
          size={244}
          busy={busy}
          onStart={onStart}
          onRefresh={onRefresh}
        />
        {mode === 'qr' && msLeft !== null ? (
          <span className={styles.validity}>Gültig noch {formatCountdown(msLeft)}</span>
        ) : null}
      </div>
      <div className={styles.instructions}>
        {mode === 'expired' ? (
          <>
            <h2 className={styles.instructionsTitle}>Der Code war 60 Sekunden gültig</h2>
            <div className={styles.warningBox}>
              Aus Sicherheitsgründen läuft jeder Code nach einer Minute ab. Laden Sie einen neuen und scannen
              Sie ihn zügig.
            </div>
            <StepList dimmed />
            {error ? <Banner tone="danger">{error}</Banner> : null}
            <div className={styles.footRow}>
              <span className={styles.footNote}>
                Kein Zugriff auf das Diensthandy? Administrator informieren
                {workspace.approvalRecipientName ? ` · ${workspace.approvalRecipientName}` : ''}
              </span>
            </div>
          </>
        ) : (
          <>
            <h2 className={styles.instructionsTitle}>So verbinden Sie Ihr Handy</h2>
            <StepList dimmed={false} />
            <div className={styles.infoBox}>
              Nachrichten laufen über Ihr Handy. Ist das Handy aus oder offline, pausiert der Versand und
              DAMOKLES weist Sie darauf hin.
            </div>
            {error ? <Banner tone="danger">{error}</Banner> : null}
            <div className={styles.footRow}>
              {mode === 'qr' ? (
                <Button
                  variant="secondary"
                  size="small"
                  className={styles.footButton}
                  loading={busy}
                  onClick={onStart}
                >
                  Neuen Code erzeugen
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="small"
                className={styles.footButtonGhost}
                onClick={() => navigate('/settings/connections')}
              >
                Später einrichten
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** The checklist step after the key exchange (D-161: the chats of the user's contacts, not „der letzten 30 Tage"). */
export const CHAT_BACKFILL_STEP = 'Chats Ihrer Kontakte laden';

/**
 * D-161: the contact-scoped chat backfill after the pairing, from the session's `progressText` — „Chats Ihrer
 * Kontakte werden geladen" while the server reports „Chats werden zugeordnet …", the server's final line verbatim
 * („WhatsApp verbunden · 84 Chats zugeordnet"), nothing for a session without a backfill line.
 */
export function chatBackfillLine(status: Pick<WhatsappStatus, 'progressText'> | undefined): string | null {
  const text = status?.progressText ?? null;
  if (text === null) return null;
  if (text.startsWith('Chats werden zugeordnet')) return 'Chats Ihrer Kontakte werden geladen';
  if (text.startsWith('WhatsApp verbunden')) return text;
  return null;
}

/** E10: the phone finishes the key exchange. */
function PairingView({ status, busy, error, onCancelPairing }: ViewProps) {
  return (
    <div className={styles.pairingCard} role="status">
      <span className={styles.spinner} aria-hidden="true" />
      <div>
        <h2 className={styles.pairingTitle}>Scan erkannt · Session wird aufgebaut</h2>
        <p className={styles.pairingText}>
          Bitte lassen Sie WhatsApp im Vordergrund, bis die Verbindung steht.
        </p>
      </div>
      <ul className={styles.checklist}>
        <li className={clsx(styles.check, styles.checkDone)}>
          <span className={styles.checkMark} aria-hidden="true">
            ✓
          </span>
          Gerät erkannt{status.phoneE164 ? ` · ${formatPhone(status.phoneE164)}` : ''}
        </li>
        <li className={clsx(styles.check, styles.checkActive)}>
          <span className={styles.checkSpinner} aria-hidden="true" />
          {status.progressText ?? 'Schlüssel werden ausgetauscht'}
        </li>
        <li className={clsx(styles.check, styles.checkOpen)}>
          <span className={styles.checkDashed} aria-hidden="true" />
          {CHAT_BACKFILL_STEP}
        </li>
      </ul>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Button variant="secondary" size="small" loading={busy} onClick={onCancelPairing}>
        Abbrechen
      </Button>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.fact}>
      <Eyebrow as="div">{label}</Eyebrow>
      <div className={styles.factValue}>{value}</div>
    </div>
  );
}

/** E11: the active session. */
function ActiveView({ status, error, onEnd, onCheck, checking }: ViewProps) {
  const chats = chatBackfillLine(status);
  return (
    <div className={styles.sessionCard}>
      <div className={styles.sessionHead}>
        <span className={clsx(styles.sessionGlyph, styles.sessionGlyphOk)} aria-hidden="true">
          ✓
        </span>
        <div>
          <h2 className={styles.sessionTitle}>Session aktiv</h2>
          <p className={styles.sessionText}>Nachrichten aus DAMOKLES werden über dieses Gerät gesendet.</p>
          {chats ? <p className={styles.sessionText}>{chats}</p> : null}
        </div>
        <Pill variant="success" size="large" dot className={styles.sessionPill}>
          Verbunden
        </Pill>
      </div>
      <div className={styles.facts}>
        <Fact label="Nummer" value={formatPhone(status.phoneE164)} />
        <Fact label="Gerät" value={status.deviceName ?? '—'} />
        <Fact
          label="Verbunden seit"
          value={
            status.connectedSince
              ? `${formatDate(status.connectedSince)}, ${formatTime(status.connectedSince)}`
              : '—'
          }
        />
        <Fact
          label="Nachrichten heute"
          value={`${status.sentToday} gesendet · ${status.receivedToday} empfangen`}
        />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className={styles.sessionActions}>
        <Button
          variant="secondary"
          size="small"
          className={styles.footButton}
          loading={checking}
          onClick={onCheck}
        >
          Verbindung prüfen
        </Button>
        <Button variant="ghost" size="small" className={styles.footButtonGhost} onClick={onEnd}>
          Session beenden
        </Button>
        <span className={styles.heartbeat}>
          {status.lastSeenAt
            ? `Letzter Herzschlag ${relativeTime(status.lastSeenAt)}`
            : 'Noch kein Herzschlag'}
        </span>
      </div>
    </div>
  );
}

/**
 * „Session unterbrochen · kein Herzschlag seit 3 Stunden 12 Minuten · bitte einen neuen QR-Code scannen" — the
 * reason line of E12; the API's `error` already starts with „Session unterbrochen", so only its remainder is added.
 */
export function disconnectedReason(
  status: Pick<WhatsappStatus, 'lastSeenAt' | 'error'>,
  now = new Date(),
): string {
  const parts = ['Session unterbrochen'];
  if (status.lastSeenAt) parts.push(`kein Herzschlag seit ${durationSince(status.lastSeenAt, now)}`);
  const detail = status.error?.replace(/^Session unterbrochen(\s*·\s*)?/, '').trim();
  if (detail) parts.push(detail);
  return parts.join(' · ');
}

/** E12: the session broke (phone offline); the outbox waits. */
function DisconnectedView({ status, busy, error, onStart }: ViewProps) {
  const count = status.pendingMessages;
  const more = Math.max(0, count - status.pending.length);
  return (
    <>
      <div className={styles.dangerBanner} role="alert">
        <div className={styles.dangerText}>
          <span className={styles.dangerTitle}>
            Die Session ist getrennt.{' '}
            {count === 1 ? '1 Nachricht wartet im Ausgang.' : `${count} Nachrichten warten im Ausgang.`}
          </span>
          <span className={styles.dangerBody}>
            {status.lastSeenAt ? `Zuletzt erreichbar ${dayTime(status.lastSeenAt)}. ` : ''}Prüfen Sie, ob das
            Diensthandy eingeschaltet und online ist.
          </span>
        </div>
        <Button className={styles.dangerButton} loading={busy} onClick={onStart}>
          Neu verbinden
        </Button>
      </div>
      <div className={styles.sessionCard}>
        <div className={styles.sessionHead}>
          <span className={clsx(styles.sessionGlyph, styles.sessionGlyphQuiet)} aria-hidden="true">
            ⌯
          </span>
          <div>
            <h2 className={styles.sessionTitle}>
              {status.phoneE164 ? formatPhone(status.phoneE164) : 'WhatsApp'}
            </h2>
            <p className={styles.sessionText}>{disconnectedReason(status)}</p>
          </div>
          <Pill variant="danger" size="large" dot className={styles.sessionPill}>
            Getrennt
          </Pill>
        </div>
        {count > 0 ? (
          <div className={styles.outbox}>
            <Eyebrow as="div" size={11}>
              Wartende Nachrichten
            </Eyebrow>
            {status.pending.map((message) => (
              <div key={message.id} className={styles.outboxRow}>
                <span className={styles.outboxTime}>{message.sendAt ? formatTime(message.sendAt) : '—'}</span>
                <span className={styles.outboxText}>
                  <span className={styles.outboxTitle}>
                    {message.contactName ? `${message.contactName} · ` : ''}
                    {message.title}
                  </span>
                  {message.error ? <span className={styles.outboxMeta}>{message.error}</span> : null}
                </span>
                <span
                  className={clsx(styles.outboxPill, message.status === 'failed' && styles.outboxPillFailed)}
                >
                  {message.status === 'failed' ? 'fehlgeschlagen' : 'im Ausgang'}
                </span>
              </div>
            ))}
            <span className={styles.outboxNote}>
              {more > 0 ? `und ${more} weitere · ` : ''}Versand startet automatisch, sobald die Session wieder
              steht
            </span>
          </div>
        ) : null}
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={styles.sessionActions}>
          <Button
            variant="secondary"
            size="small"
            className={styles.footButton}
            loading={busy}
            onClick={onStart}
          >
            QR-Code anzeigen
          </Button>
        </div>
      </div>
    </>
  );
}

/** EM4: QR (or expired) with the pairing card below; EM5: active/disconnected cards. */
function MobileView(props: ViewProps) {
  const navigate = useNavigate();
  const { status, state, msLeft, busy, error, onStart, onRefresh, onEnd } = props;
  if (state === 'connected' || state === 'disconnected') {
    const count = status.pendingMessages;
    return (
      <div className={styles.mobileStack}>
        {state === 'connected' ? (
          <div className={styles.mobileCard}>
            <div className={styles.mobileHead}>
              <span className={clsx(styles.mobileGlyph, styles.sessionGlyphOk)} aria-hidden="true">
                ✓
              </span>
              <div>
                <div className={styles.mobileTitle}>Session aktiv</div>
                <div className={styles.mobileSub}>{formatPhone(status.phoneE164)}</div>
              </div>
            </div>
            <div className={styles.mobileFacts}>
              <div className={styles.mobileFact}>
                <span>Gerät</span>
                <span>{status.deviceName ?? '—'}</span>
              </div>
              <div className={styles.mobileFact}>
                <span>Seit</span>
                <span>
                  {status.connectedSince
                    ? `${formatDate(status.connectedSince)}, ${formatTime(status.connectedSince)}`
                    : '—'}
                </span>
              </div>
              <div className={styles.mobileFact}>
                <span>Heute</span>
                <span>
                  {status.sentToday} gesendet · {status.receivedToday} empfangen
                </span>
              </div>
            </div>
            {error ? <Banner tone="danger">{error}</Banner> : null}
            <Button variant="ghost" size="large" onClick={onEnd}>
              Session beenden
            </Button>
          </div>
        ) : (
          <div className={clsx(styles.mobileCard, styles.mobileCardDanger)}>
            <div className={styles.mobileDangerTitle}>
              <span className={styles.mobileDangerDot} aria-hidden="true" />
              Zustand: getrennt
            </div>
            <p className={styles.mobileDangerText}>
              {status.lastSeenAt ? `Kein Herzschlag seit ${durationSince(status.lastSeenAt)}. ` : ''}
              {count === 1
                ? '1 Nachricht wartet im Ausgang und geht automatisch raus, sobald die Session steht.'
                : `${count} Nachrichten warten im Ausgang und gehen automatisch raus, sobald die Session steht.`}
            </p>
            {error ? <Banner tone="danger">{error}</Banner> : null}
            <Button size="large" loading={busy} onClick={onStart}>
              Neu verbinden
            </Button>
          </div>
        )}
        <div className={styles.mobileNote}>
          Beim Beenden werden wartende Nachrichten verworfen. Chats bleiben in der Kontaktakte.
        </div>
      </div>
    );
  }
  const mode = qrModeOf(state);
  return (
    <div className={styles.mobileStack}>
      {state !== 'pairing' ? (
        <div className={clsx(styles.mobileCard, styles.mobileQrCard)}>
          <QrBox
            mode={mode}
            qrDataUrl={status.qrDataUrl}
            progressText={status.progressText}
            size={210}
            busy={busy}
            onStart={onStart}
            onRefresh={onRefresh}
          />
          {mode === 'qr' && msLeft !== null ? (
            <span className={styles.validity}>Gültig noch {formatCountdown(msLeft)}</span>
          ) : null}
          <span className={styles.mobileQrText}>
            Auf dem Diensthandy WhatsApp öffnen und diesen Code scannen.
          </span>
        </div>
      ) : null}
      {state === 'pairing' ? (
        <div className={clsx(styles.mobileCard, styles.mobilePairing)} role="status">
          <span className={styles.checkSpinner} aria-hidden="true" />
          <div>
            <div className={styles.mobilePairingTitle}>Scan erkannt</div>
            <div className={styles.mobileSub}>
              {status.progressText ?? 'Schlüssel werden ausgetauscht'}
              {status.phoneE164 ? ` · ${formatPhone(status.phoneE164)}` : ''}
            </div>
          </div>
        </div>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {state === 'pairing' ? (
        <Button variant="secondary" size="large" loading={busy} onClick={props.onCancelPairing}>
          Abbrechen
        </Button>
      ) : mode === 'starting' ? null : (
        <Button
          variant="secondary"
          size="large"
          loading={busy}
          onClick={mode === 'none' ? onStart : onRefresh}
        >
          {mode === 'none' ? 'QR-Code anzeigen' : 'Neuen Code laden'}
        </Button>
      )}
      <Button variant="ghost" size="large" onClick={() => navigate('/settings/connections')}>
        Später einrichten
      </Button>
    </div>
  );
}

/**
 * `/settings/whatsapp` — Einstellungen 08–13 (EM4/EM5 on mobile), the seven states of `GET /connections/whatsapp`
 * on the organization's own bridge (P-46, D-55): none · starting · qr · expired · pairing · connected · disconnected.
 * Start and refresh answer 202 with the state the bridge reached; the status query is polled while it moves.
 */
export function WhatsAppSettingsPage() {
  const mobile = useIsMobile();
  const { toast } = useToast();
  const status = useWhatsappStatus();
  const start = useStartWhatsapp();
  const refresh = useRefreshWhatsapp();
  const end = useEndWhatsapp();
  const [error, setError] = useState<string | null>(null);
  const [endOpen, setEndOpen] = useState(false);
  const msLeft = useCountdown(status.data?.state === 'qr' ? status.data.expiresAt : null);
  const serverState = status.data?.state ?? 'none';
  const state: WhatsappState =
    serverState === 'qr' && msLeft !== null && msLeft <= 0 ? 'expired' : serverState;
  const busy = start.isPending || refresh.isPending || end.isPending;

  async function run(action: () => Promise<unknown>, success?: string) {
    setError(null);
    try {
      await action();
      if (success) toast({ message: success });
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  const onStart = () => void run(() => start.mutateAsync());
  const onRefresh = () => void run(() => refresh.mutateAsync());
  const onCancelPairing = () => void run(() => end.mutateAsync(), 'Verbindungsaufbau abgebrochen.');
  const onCheck = () => void run(() => status.refetch());

  async function confirmEnd() {
    await run(() => end.mutateAsync(), 'Session beendet.');
    setEndOpen(false);
  }

  const headerTitle =
    state === 'connected' || state === 'disconnected' || state === 'pairing'
      ? 'WhatsApp-Session'
      : 'WhatsApp-Session öffnen';
  const headerText =
    state === 'connected'
      ? 'Nachrichten aus DAMOKLES laufen über das Diensthandy.'
      : state === 'disconnected'
        ? 'Die Session ist unterbrochen — wartende Nachrichten gehen automatisch raus, sobald sie wieder steht.'
        : state === 'pairing'
          ? 'Das Diensthandy schließt die Verbindung gerade ab.'
          : 'Scannen Sie den Code mit WhatsApp auf Ihrem Diensthandy. Die Session bleibt offen, bis Sie sie beenden.';

  const viewProps: ViewProps | null = status.data
    ? {
        status: status.data,
        state,
        msLeft,
        busy,
        error,
        onStart,
        onRefresh,
        onCancelPairing,
        onEnd: () => setEndOpen(true),
        onCheck,
        checking: status.isFetching && !busy,
      }
    : null;

  return (
    <div className={clsx(styles.page, mobile && styles.pageMobile)}>
      <div className={styles.column}>
        <SettingsPageHeader eyebrow="Verbindungen" title={headerTitle} description={headerText} />
        {status.isError ? (
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void status.refetch()}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(status.error)}
          </Banner>
        ) : null}
        {status.isPending ? (
          <div className={styles.skeleton} aria-busy="true">
            <Skeleton variant="block" height={mobile ? 320 : 300} />
          </div>
        ) : viewProps ? (
          mobile ? (
            <MobileView {...viewProps} />
          ) : state === 'pairing' ? (
            <PairingView {...viewProps} />
          ) : state === 'connected' ? (
            <ActiveView {...viewProps} />
          ) : state === 'disconnected' ? (
            <DisconnectedView {...viewProps} />
          ) : (
            <QrView {...viewProps} />
          )
        ) : null}
      </div>
      <Dialog
        open={endOpen}
        onClose={() => setEndOpen(false)}
        title="Session wirklich beenden?"
        width={480}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEndOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={end.isPending} onClick={() => void confirmEnd()}>
              Session beenden
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>
          DAMOKLES kann danach keine WhatsApp-Nachrichten senden oder empfangen. Bereits geführte Chats
          bleiben in der Kontaktakte erhalten. Zum erneuten Verbinden scannen Sie einen neuen QR-Code.
        </p>
        {status.data && status.data.pendingMessages > 0 ? (
          <div className={styles.dialogWarning}>
            {status.data.pendingMessages === 1
              ? '1 Nachricht im Ausgang wird verworfen.'
              : `${status.data.pendingMessages} Nachrichten im Ausgang werden verworfen.`}
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
